import { App } from "vue";

// ENG-21695 minimal WebUSB typings — @types/w3c-web-usb isn't installed and the
// DOM lib doesn't ship navigator.usb. Only the surface this service touches is typed.
interface UsbEndpoint {
  direction: string;
  endpointNumber: number;
}
interface UsbAlternateInterface {
  endpoints: UsbEndpoint[];
}
interface UsbInterface {
  interfaceNumber: number;
  alternates: UsbAlternateInterface[];
}
interface UsbConfiguration {
  interfaces: UsbInterface[];
}
interface UsbDevice {
  vendorId: number;
  productId: number;
  productName: string;
  manufacturerName: string;
  configuration: UsbConfiguration | null;
  open(): Promise<void>;
  close(): Promise<void>;
  selectConfiguration(configurationValue: number): Promise<void>;
  claimInterface(interfaceNumber: number): Promise<void>;
  releaseInterface(interfaceNumber: number): Promise<void>;
  selectAlternateInterface(interfaceNumber: number, alternateSetting: number): Promise<void>;
  transferOut(endpointNumber: number, data: BufferSource): Promise<unknown>;
}
interface Usb {
  getDevices(): Promise<UsbDevice[]>;
  requestDevice(options: {
    filters: Array<{ vendorId?: number; productId?: number }>;
  }): Promise<UsbDevice>;
}

declare global {
  interface Navigator {
    usb: Usb;
  }
  interface Window {
    labelPrintService: ReturnType<typeof LabelPrintService>;
  }
}

export function labelPrintService() {
  return {
    install(app: App) {
      const globalProperties = app.config.globalProperties;
      window.labelPrintService = globalProperties.$labelPrintService = LabelPrintService();
    },
  };
}

export const LabelPrintService = () => {
  // Cached for the lifetime of one print sequence; cleared in print()'s finally
  // so the next print on this tab — and concurrent prints on other tabs — can
  // claim the USB interface afresh.
  let cachedDevice: UsbDevice | null | undefined;
  let connected = false;

  const getSavedDevice = async () => {
    const saved = JSON.parse(localStorage.getItem("zebraPrinterInfo") ?? "null");
    if (!saved) return null;

    const devices = await navigator.usb.getDevices();
    return devices.find((d) => d.vendorId === saved.vendorId && d.productId === saved.productId);
  };

  const getSavedOffsetX = () => {
    const saved = JSON.parse(localStorage.getItem("zebraPrinterInfo") ?? "null");
    return saved?.offsetX ?? 0;
  };

  // ENG-21803 label content sits at a fixed dot inset with no horizontal
  // calibration, so registration drift on a given printer shows up as a
  // consistent left/right shift. Nudge it with the printer's own home/reference
  // command rather than reissuing every coordinate from the server.
  const applyOffset = (rawData: string, deviceType: string | null, offsetX: number) => {
    if (!offsetX) return rawData;
    if ("ZPL" === deviceType) return rawData.replace(/\^XA/g, `^XA\n^LH${offsetX},0`);
    if ("EPL" === deviceType) return rawData.replace(/^N$/gm, `R${offsetX},0\nN`);
    return rawData;
  };

  const requestAndSaveDevice = async () => {
    try {
      const device = await navigator.usb.requestDevice({
        filters: [{ vendorId: 0x0a5f }],
      });

      // ENG-21803 merge rather than overwrite — a horizontal offset saved
      // before this device was ever paired would otherwise be wiped here.
      const saved = JSON.parse(localStorage.getItem("zebraPrinterInfo") ?? "null") ?? {};
      localStorage.setItem(
        "zebraPrinterInfo",
        JSON.stringify({
          ...saved,
          vendorId: device.vendorId,
          productId: device.productId,
        }),
      );

      return device;
    } catch (error) {
      if ((error as { name?: string }).name === "NotFoundError") {
        console.warn("User cancelled the USB device selection.");
      } else {
        console.error("Unexpected error when selecting USB device:", error);
      }
      return null;
    }
  };

  const initDevice = async () => {
    const device = await getDevice();
    const usbInterface = await getUsbInterface();

    await device!.claimInterface(usbInterface!.interfaceNumber);
    await device!.selectAlternateInterface(usbInterface!.interfaceNumber, 0);

    return { device, interfaceNumber: usbInterface!.interfaceNumber };
  };

  const getDevice = async (): Promise<UsbDevice | null | undefined> => {
    if (cachedDevice) {
      return cachedDevice;
    }

    cachedDevice = await getSavedDevice();
    if (!cachedDevice) {
      cachedDevice = await requestAndSaveDevice();
    }

    if (!cachedDevice) {
      return;
    }

    await cachedDevice.open();
    if (cachedDevice.configuration === null) {
      await cachedDevice.selectConfiguration(1);
    }

    return cachedDevice;
  };

  const releaseDevice = async (device: UsbDevice, interfaceNumber: number) => {
    try {
      await device.releaseInterface(interfaceNumber);
    } catch (e) {
      console.warn("Failed to release USB interface:", e);
    }
    try {
      await device.close();
    } catch (e) {
      console.warn("Failed to close USB device:", e);
    }
    cachedDevice = undefined;
  };

  const getUsbInterface = async () => {
    const device = await getDevice();

    return device!.configuration!.interfaces.find((i) =>
      i.alternates.some((a) => a.endpoints.some((e) => e.direction === "out")),
    );
  };

  const getEndpoint = async () => {
    const usbInterface = await getUsbInterface();

    return usbInterface!.alternates[0].endpoints.find((e) => e.direction === "out");
  };

  const getDeviceType = async () => {
    const device = await getDevice();
    if (!device) {
      return null;
    }

    if (
      device.productName.trim() == "LP2824" ||
      device.productName.trim() == "ZTC LP 2824 Plus" ||
      device.productName.trim() == "ZTC TLP 2824 Plus" ||
      device.productName.substring(device.productName.length - 8) === "TPL 2844"
    ) {
      return "EPL";
    } else if (
      device.productName.trim() == "LP2824-Z" ||
      device.productName.trim() == "ZTC ZD411-203dpi ZPL" ||
      device.productName.trim() == "ZTC ZD220-203dpi ZPL" ||
      device.productName.substring(device.productName.length - 4) === " ZPL" ||
      device.productName.substring(device.productName.length - 10) === "TPL 2844-Z" ||
      device.productName.match(new RegExp(/G[KV]420D/, "i")) !== null
    ) {
      return "ZPL";
    }

    return null;
  };

  const print = async (rawData: string) => {
    let device;
    let interfaceNumber;
    try {
      ({ device, interfaceNumber } = await initDevice());
      const deviceType = await getDeviceType();
      const endpoint = await getEndpoint();
      const offsetX = getSavedOffsetX();

      if ("EPL" === deviceType) {
        const encoder = new TextEncoder();
        const data = encoder.encode(applyOffset(rawData, deviceType, offsetX));
        await device!.transferOut(endpoint!.endpointNumber, data);

        return;
      } else if ("ZPL" === deviceType) {
        const encoder = new TextEncoder();
        // ENG-20690 switch the printer to UTF-8 input so multi-byte accented chars
        // in patient names render correctly. The trailing ^JUS save persists it.
        await device!.transferOut(endpoint!.endpointNumber, encoder.encode("^XA^CI28^XZ\n"));
        const data = encoder.encode(applyOffset(rawData, deviceType, offsetX));
        await device!.transferOut(endpoint!.endpointNumber, data);
        await device!.transferOut(endpoint!.endpointNumber, encoder.encode(`^XA\n^JUS\n^XZ`));

        return;
      }

      alert("Could not determine printer model: " + device!.productName.trim());
    } catch (error) {
      console.error(error);
      alert("Error: " + (error as { message?: string }).message);
    } finally {
      if (device) {
        await releaseDevice(device, interfaceNumber!);
      }
    }
  };

  const connect = async () => {
    // ENG-21148 open only — don't claim the interface so other tabs can still
    // print. printRaw claims and releases per transfer while keeping the device
    // open to avoid the rapid close→reopen cycle that confuses the ZD411.
    cachedDevice = await getDevice();
    connected = true;
    return {
      productName: cachedDevice!.productName,
      manufacturerName: cachedDevice!.manufacturerName,
      vendorId: cachedDevice!.vendorId,
      productId: cachedDevice!.productId,
    };
  };

  const isConnected = () => connected;

  const disconnect = async () => {
    if (!connected || !cachedDevice) return;
    try {
      await cachedDevice.close();
    } catch (e) {
      console.warn("Failed to close USB device:", e);
    }
    cachedDevice = undefined;
    connected = false;
  };

  const printRaw = async (zpl: string) => {
    const deviceType = await getDeviceType();
    const data = applyOffset(zpl, deviceType, getSavedOffsetX());

    if (connected && cachedDevice) {
      // Device stays open between prints (avoids the close→reopen timing issue
      // on the ZD411). Claim→transfer→release per print so other tabs can
      // still use the printer between our jobs.
      // The ^CI28 preamble matches what print() sends — needed to stabilise the
      // endpoint after selectAlternateInterface before label data arrives.
      const usbInterface = await getUsbInterface();
      const interfaceNumber = usbInterface!.interfaceNumber;
      await cachedDevice!.claimInterface(interfaceNumber);
      await cachedDevice!.selectAlternateInterface(interfaceNumber, 0);
      const endpoint = await getEndpoint();
      const encoder = new TextEncoder();
      try {
        await cachedDevice!.transferOut(endpoint!.endpointNumber, encoder.encode(data));
      } finally {
        await cachedDevice!.releaseInterface(interfaceNumber);
      }
      return;
    }
    const { device, interfaceNumber } = await initDevice();
    const endpoint = await getEndpoint();
    try {
      await device!.transferOut(endpoint!.endpointNumber, new TextEncoder().encode(data));
    } finally {
      await releaseDevice(device!, interfaceNumber);
    }
  };

  return {
    getDeviceType,
    print,
    connect,
    isConnected,
    disconnect,
    printRaw,
  };
};
